import { db } from './firebase';
import {
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';
import type { VendorPortalUserPrefs } from '../types';

export { shouldAutoStartTour } from './vendorPortalTourPrefs';

const COLLECTION = 'vendorPortalUserPrefs';

function mapPrefsDoc(data: DocumentData): VendorPortalUserPrefs {
  const tourCompletedByOrg: Record<string, Date> = {};
  const rawCompleted = data.tourCompletedByOrg;
  if (rawCompleted && typeof rawCompleted === 'object') {
    Object.entries(rawCompleted as Record<string, unknown>).forEach(([orgId, value]) => {
      const date =
        value instanceof Timestamp
          ? value.toDate()
          : value instanceof Date
            ? value
            : null;
      if (date) tourCompletedByOrg[orgId] = date;
    });
  }

  const tourSkippedByOrg: Record<string, Date> = {};
  const rawSkipped = data.tourSkippedByOrg;
  if (rawSkipped && typeof rawSkipped === 'object') {
    Object.entries(rawSkipped as Record<string, unknown>).forEach(([orgId, value]) => {
      const date =
        value instanceof Timestamp
          ? value.toDate()
          : value instanceof Date
            ? value
            : null;
      if (date) tourSkippedByOrg[orgId] = date;
    });
  }

  return {
    tourCompletedByOrg,
    tourSkippedByOrg,
    updatedAt:
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate()
        : data.updatedAt instanceof Date
          ? data.updatedAt
          : undefined,
  };
}

export function subscribeVendorPortalUserPrefs(
  uid: string,
  onData: (prefs: VendorPortalUserPrefs | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const ref = doc(db, COLLECTION, uid);
  return onSnapshot(
    ref,
    snap => {
      if (!snap.exists()) {
        onData(null);
        return;
      }
      onData(mapPrefsDoc(snap.data()));
    },
    err => {
      console.warn('[VendorPortal] user prefs listen failed:', err);
      onData(null);
      onError?.(err);
    },
  );
}

async function mergeOrgTimestamp(
  uid: string,
  field: 'tourCompletedByOrg' | 'tourSkippedByOrg',
  orgId: string,
): Promise<void> {
  const ref = doc(db, COLLECTION, uid);
  const now = Timestamp.now();
  try {
    await setDoc(
      ref,
      {
        [field]: { [orgId]: now },
        updatedAt: now,
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[VendorPortal] could not save user prefs:', err);
  }
}

export async function markVendorPortalTourCompleted(uid: string, orgId: string): Promise<void> {
  await mergeOrgTimestamp(uid, 'tourCompletedByOrg', orgId);
}

export async function markVendorPortalTourSkipped(uid: string, orgId: string): Promise<void> {
  await mergeOrgTimestamp(uid, 'tourSkippedByOrg', orgId);
}
